import styled, { CSSProperties, useTheme } from "styled-components";
import CheckIcon from "@/assets/icons/Check.svg";
import CloseIcon from "@/assets/icons/CloseIcon.svg";
import React, { ReactNode } from "react";
import { Icon } from "../Icon/Icon";

interface CheckboxProps {
  label?: string | ReactNode;
  toggled: boolean;
  clickedFunction?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  size?: string;
  iconSize?: string;
  disabled?: boolean;
  isOverdue?: boolean;
  style?: CSSProperties;
}

interface StyledCheckboxProps {
  $disabled?: boolean;
  $toggled: boolean;
  $size?: string;
  $isOverdue?: boolean;
  $iconSize?: string;
}

const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
`;

const StyledCheckbox = styled.div<StyledCheckboxProps>`
  background-color: ${({ $toggled, theme, $isOverdue }) =>
    $isOverdue ? theme.main : $toggled ? theme.green : "#fff"};
  border: 1px solid
    ${({ $toggled, $disabled, theme, $isOverdue }) =>
      $disabled
        ? theme.cardBorder
        : $isOverdue
          ? theme.main
          : $toggled
            ? theme.green
            : theme.cardBorder};
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${({ $size }) => $size || "14px"};
  width: ${({ $size }) => $size || "14px"};
  cursor: ${({ $disabled, $isOverdue }) =>
    $disabled || $isOverdue ? "not-allowed" : "pointer"};
  pointer-events: ${({ $disabled, $isOverdue }) =>
    $disabled || $isOverdue ? "none" : "auto"};
  flex-shrink: 0;

  .overdue-icon {
    height: ${({ $iconSize }) =>
      `calc(${$iconSize} - 3px)` || "14px"} !important;
    width: ${({ $iconSize }) =>
      `calc(${$iconSize} - 3px)` || "14px"} !important;
    mask-size: ${({ $iconSize }) =>
      `calc(${$iconSize} - 3px) calc(${$iconSize} - 3px)`} !important;
  }
`;

const CheckboxLabel = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  user-select: none;
  gap: 10px;

  color: ${(props) => (props.$disabled ? props.theme.cardBorder : "revert")};
`;

export default function Checkbox({
  label,
  toggled,
  clickedFunction,
  size = "16px",
  disabled = false,
  iconSize = "13px",
  isOverdue,
  style,
}: CheckboxProps) {
  const theme = useTheme();
  return (
    <CheckboxLabel
      $disabled={disabled}
      style={style}
      onClick={(e) => {
        const parent = e.currentTarget.parentElement;
        if (!parent?.classList.contains("dropdown-check")) {
          e.stopPropagation(); // prevent parent card click
        }
      }}
    >
      <HiddenCheckbox
        checked={toggled}
        onChange={clickedFunction}
        disabled={disabled}
        aria-checked={toggled}
        readOnly={!clickedFunction}
      />
      <StyledCheckbox
        $toggled={toggled}
        $disabled={disabled}
        $size={size}
        $isOverdue={isOverdue}
        $iconSize={iconSize}
      >
        {toggled && (
          <Icon
            url={CheckIcon.src}
            width={iconSize}
            height={iconSize}
            color={theme.white}
          />
        )}
        {isOverdue && (
          <Icon
            url={CloseIcon.src}
            width={iconSize}
            height={iconSize}
            color={theme.white}
            customClass="overdue-icon"
          />
        )}
      </StyledCheckbox>
      {label && typeof label === "string" ? <span>{label}</span> : label}
    </CheckboxLabel>
  );
}
